import { ChevronRight } from 'lucide-react';
import type { MavisOpportunityRow } from '../../types/mavis';
import MavisStageChip from './MavisStageChip';
import MavisForecastLabelChip from './MavisForecastLabelChip';

function formatAmount(amount: number | null, currency: string): string {
  if (amount === null) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toLocaleString()}`;
  }
}

function formatCloseDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  // Append time to avoid timezone-offset date shifts
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatUpdated(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface Props {
  opportunity: MavisOpportunityRow;
  ownerName: string | null;
  onClick: () => void;
}

export default function MavisForecastRow({ opportunity: opp, ownerName, onClick }: Props) {
  return (
    <tr
      onClick={onClick}
      className="border-b border-app-line last:border-b-0 hover:bg-forest-50/40 cursor-pointer transition-colors group"
    >
      <td className="px-4 py-3 text-sm font-medium text-app-ink max-w-[160px] truncate">
        {opp.accounts?.company_name ?? <span className="text-app-muted">—</span>}
      </td>
      <td className="px-4 py-3 text-sm text-app-ink max-w-xs truncate">
        {opp.title}
      </td>
      <td className="px-4 py-3">
        <MavisStageChip stage={opp.stage} />
      </td>
      <td className="px-4 py-3 text-sm text-app-ink text-right tabular-nums">
        {formatAmount(opp.amount, opp.currency)}
      </td>
      <td className="px-4 py-3 text-sm text-app-muted whitespace-nowrap">
        {formatCloseDate(opp.target_close_date)}
      </td>
      <td className="px-4 py-3">
        {opp.forecast_label
          ? <MavisForecastLabelChip label={opp.forecast_label} />
          : <span className="text-app-muted text-sm">—</span>
        }
      </td>
      <td className="px-4 py-3 text-sm text-app-muted text-right tabular-nums">
        {opp.forecast_percentage !== null ? `${opp.forecast_percentage}%` : '—'}
      </td>
      <td className="px-4 py-3 text-sm text-app-muted max-w-[120px] truncate">
        {ownerName ?? <span className="text-app-muted">—</span>}
      </td>
      <td className="px-4 py-3 text-xs text-app-muted whitespace-nowrap">
        {formatUpdated(opp.updated_at)}
      </td>
      <td className="px-3 py-3 text-app-muted group-hover:text-app-muted transition-colors">
        <ChevronRight className="w-4 h-4" />
      </td>
    </tr>
  );
}
